// The session token (spec/dashboard.md section 3.2). It lives in localStorage so every tab of the browser shares it: a deep
// link opened in a new tab, a Ctrl-click or a duplicated tab stays signed in, and AuthContext mirrors a sign-in or sign-out
// made in another tab through the `storage` event. When localStorage is unavailable (private mode, blocked site data) the
// token falls back to sessionStorage, then to memory. A session an older build kept in sessionStorage is adopted on the
// first read so nobody is signed out by the upgrade. An expired token is dropped on read.
export const SESSION_KEY = 'huelency.session';
const KEY = SESSION_KEY;

export interface StoredSession {
  token: string;
  expiresAt: string;
  email: string;
}

/** the last resort when no Web Storage works */
let memory: string | null = null;

function local(): Storage | null {
  try {
    // reading the property itself throws when site data is blocked
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function legacy(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function read(): string | null {
  const l = local();
  if (l) {
    const raw = l.getItem(KEY);
    if (raw) return raw;
    // a session saved per tab before the move to localStorage: adopt it
    const old = legacy()?.getItem(KEY) ?? null;
    if (old) {
      try {
        l.setItem(KEY, old);
        legacy()?.removeItem(KEY);
      } catch {
        // quota: keep reading it from sessionStorage
      }
    }
    return old ?? memory;
  }
  return legacy()?.getItem(KEY) ?? memory;
}

/** The stored session, or null when there is none, it does not parse or it has expired. */
export function parseSession(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.token || new Date(parsed.expiresAt).getTime() <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function loadSession(): StoredSession | null {
  try {
    const raw = read();
    if (!raw) return null;
    const parsed = parseSession(raw);
    if (!parsed) clearSession();
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession): void {
  const raw = JSON.stringify(session);
  try {
    const l = local();
    if (l) {
      l.setItem(KEY, raw);
      legacy()?.removeItem(KEY);
      memory = null;
      return;
    }
  } catch {
    // private mode refuses the write: fall back to the tab
  }
  const s = legacy();
  if (s) {
    try {
      s.setItem(KEY, raw);
      return;
    } catch {
      // fall through to memory
    }
  }
  memory = raw;
}

export function clearSession(): void {
  memory = null;
  try {
    local()?.removeItem(KEY);
  } catch {
    // unavailable
  }
  try {
    legacy()?.removeItem(KEY);
  } catch {
    // unavailable
  }
}
